import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useSnackbar } from "notistack";
import {
  getGoalBlockUsers,
  toggleGoalBlockUser,
} from "../../../../redux/features/appraisalSlice";

const PAGE_SIZE = 20;

// A small, network-free colour for the initials avatar. Kept deterministic per
// user so the same person always gets the same colour.
const AVATAR_COLORS = [
  "#2F72B3", "#9C27B0", "#00897B", "#F57C00",
  "#5E35B1", "#C2185B", "#3949AB", "#00838F",
];
const colorFor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];
const initialsOf = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

/**
 * Config screen (Appraisal Operations > Config).
 *
 * Lists active users with a per-user toggle that controls whether they can add
 * goals. When a user is "blocked", their "Add Goal" button (Appraisal > Goal
 * tab) is disabled.
 *
 *   Toggle ON  (checked)   -> user is BLOCKED  (cannot add goals)
 *   Toggle OFF (unchecked) -> user is allowed  (can add goals)
 *
 * The All / Blocked / Allowed tabs let the operations team see who is currently
 * blocked without scrolling the whole company; the tab labels carry the counts.
 *
 * Performance: users are loaded one page at a time via infinite scroll, and the
 * search box queries the server (debounced) instead of loading everyone at once.
 * Avatars are initials-only (no external image requests) so the list stays fast.
 */

// The filter tabs, in the order they appear. `key` is what the API expects.
const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "blocked", label: "Blocked" },
  { key: "allowed", label: "Allowed" },
];

const Config = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all"); // which filter tab is active
  const [counts, setCounts] = useState({ all: 0, blocked: 0, allowed: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false); // first page / new search
  const [loadingMore, setLoadingMore] = useState(false); // next pages
  const [savingIds, setSavingIds] = useState([]); // rows with a toggle in flight

  const pageRef = useRef(1);
  const scrollRef = useRef(null);
  // Bumped on every new search so a stale in-flight request can be ignored.
  const requestIdRef = useRef(0);

  // Fetch a page. When page === 1 we replace the list; otherwise we append.
  const fetchPage = useCallback(
    async (page, searchTerm, statusFilter) => {
      const reqId = ++requestIdRef.current;
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const res = await dispatch(
          getGoalBlockUsers({ page, limit: PAGE_SIZE, search: searchTerm, status: statusFilter })
        ).unwrap();

        // Ignore responses from an older search/page that finished late.
        if (reqId !== requestIdRef.current) return;

        if (res?.status !== "success") throw new Error(res?.message || "Failed to load users");

        const rows = res?.data || [];
        const meta = res?.meta || {};
        setUsers((prev) => (page === 1 ? rows : [...prev, ...rows]));
        if (meta.counts) setCounts(meta.counts);
        setHasMore(!!meta.hasMore);
        pageRef.current = page;
      } catch (err) {
        if (reqId === requestIdRef.current) {
          enqueueSnackbar("Failed to load users. Please try again.", { variant: "error" });
        }
      } finally {
        // Clear the spinners even for a stale response, otherwise a superseded
        // request leaves `loadingMore` stuck on and infinite scroll stops firing.
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [dispatch, enqueueSnackbar]
  );

  // Debounce the search box: wait 400ms after typing stops, then fetch page 1.
  // Switching tabs runs through here too, so the list always restarts at page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      fetchPage(1, search.trim(), status);
    }, 400);
    return () => clearTimeout(t);
  }, [search, status, fetchPage]);

  // Infinite scroll: load the next page when near the bottom of the list.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      fetchPage(pageRef.current + 1, search.trim(), status);
    }
  };

  const handleToggle = async (user) => {
    const newBlocked = !user.isBlocked;

    // Optimistically flip the flag and move the counts, so the tab labels stay
    // in step with the row the admin just changed.
    setUsers((prev) =>
      prev.map((u) => (u.userID === user.userID ? { ...u, isBlocked: newBlocked } : u))
    );
    const applyCountDelta = (blocked) =>
      setCounts((prev) => ({
        ...prev,
        blocked: prev.blocked + (blocked ? 1 : -1),
        allowed: prev.allowed + (blocked ? -1 : 1),
      }));
    applyCountDelta(newBlocked);
    setSavingIds((prev) => [...prev, user.userID]);

    try {
      const res = await dispatch(
        toggleGoalBlockUser({ userId: user.userID, isBlocked: newBlocked })
      ).unwrap();

      if (res?.status === "success") {
        enqueueSnackbar(
          newBlocked
            ? `${user.name} is now blocked from adding goals`
            : `${user.name} can add goals again`,
          { variant: newBlocked ? "warning" : "success" }
        );
      } else {
        throw new Error(res?.message || "Failed to update");
      }
    } catch (err) {
      // Roll back the optimistic change on failure.
      setUsers((prev) =>
        prev.map((u) => (u.userID === user.userID ? { ...u, isBlocked: user.isBlocked } : u))
      );
      applyCountDelta(!newBlocked);
      enqueueSnackbar(err?.message || "Failed to update. Please try again.", { variant: "error" });
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== user.userID));
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Heading + summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
            Add Goal User Access
          </Typography>
          {/* <Typography sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
            Turn a user ON to block their "Add Goal" button. Turn OFF to allow.
          </Typography> */}
        </Box>
        <Chip
          size="small"
          color="secondary"
          variant="outlined"
          label={`${counts.all} user${counts.all === 1 ? "" : "s"}`}
          sx={{ fontSize: "0.6rem" }}
        />
      </Stack>

      {/* Search (server-side, debounced) */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search user by name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoComplete="off"
        sx={{ mb: 1, "& .MuiInputBase-input": { fontSize: "0.7rem" } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="mdi:magnify" />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={14} color="secondary" />
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Filter tabs. The counts come from the server and cover every user who
          matches the search box, not just the pages loaded so far. */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
        {STATUS_TABS.map((tab) => {
          const active = status === tab.key;
          return (
            <Chip
              key={tab.key}
              size="small"
              clickable
              onClick={() => setStatus(tab.key)}
              variant={active ? "filled" : "outlined"}
              color={tab.key === "blocked" ? "warning" : tab.key === "allowed" ? "success" : "secondary"}
              label={`${tab.label} ${counts[tab.key] ?? 0}`}
              sx={{ fontSize: "0.6rem", fontWeight: active ? 700 : 500 }}
            />
          );
        })}
      </Stack>

      {/* List */}
      {loading && users.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} color="secondary" />
        </Box>
      ) : users.length === 0 ? (
        <Typography
          sx={{ fontSize: "0.7rem", color: "text.secondary", py: 3, textAlign: "center" }}
        >
          {status === "blocked" && !search.trim()
            ? "No one is blocked from adding goals right now."
            : "No users found."}
        </Typography>
      ) : (
        <Stack
          ref={scrollRef}
          onScroll={handleScroll}
          spacing={0.75}
          sx={{ maxHeight: "60vh", overflowY: "auto", pr: 0.5 }}
        >
          {users.map((user) => {
            const isSaving = savingIds.includes(user.userID);
            return (
              <Stack
                key={user.userID}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: user.isBlocked ? "warning.light" : "divider",
                  backgroundColor: user.isBlocked
                    ? "rgba(255, 167, 38, 0.08)"
                    : "background.paper",
                }}
              >
                {/* Initials-only avatar — no network request, keeps the list fast */}
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.7rem",
                    bgcolor: colorFor(user.userID),
                  }}
                >
                  {initialsOf(user.name)}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                    {user.name || "N/A"}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.6rem", color: "text.secondary" }}>
                    {[user.designationName, user.departmentName]
                      .filter(Boolean)
                      .join(" • ") || "—"}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={user.isBlocked ? "Blocked" : "Allowed"}
                  color={user.isBlocked ? "warning" : "success"}
                  variant="outlined"
                  sx={{ fontSize: "0.55rem", height: 18 }}
                />

                {isSaving ? (
                  <CircularProgress size={16} color="secondary" />
                ) : (
                  <Switch
                    size="small"
                    color="warning"
                    checked={!!user.isBlocked}
                    onChange={() => handleToggle(user)}
                    inputProps={{ "aria-label": `Block ${user.name}` }}
                  />
                )}
              </Stack>
            );
          })}

          {/* Bottom loader while the next page is being fetched */}
          {loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <CircularProgress size={18} color="secondary" />
            </Box>
          )}
          {!hasMore && users.length > 0 && (
            <Typography
              sx={{ fontSize: "0.55rem", color: "text.disabled", textAlign: "center", py: 1 }}
            >
              End of list
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default Config;
